import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Lines } from "../components/Reveal";
import { useSiteImages } from "../lib/siteContent";

const MEDIA = {
    desktop: { video: "/assets/HERO_DESKTOP.mp4", image: "/assets/HERO_DESKTOP.jpg" },
    mobile: { video: "/assets/HERO_MOBILE.mp4", image: "/assets/HERO_MOBILE.jpg" },
};

function HeroMedia({ kind, className, image }) {
    const [videoUrl, setVideoUrl] = useState(null);
    const media = MEDIA[kind];

    useEffect(() => {
        let alive = true;
        fetch(media.video, { method: "HEAD" })
            .then((r) => {
                const type = r.headers.get("content-type") || "";
                if (alive && r.ok && !type.includes("text/html")) setVideoUrl(media.video);
            })
            .catch(() => {});
        return () => {
            alive = false;
        };
    }, [media.video]);

    if (videoUrl) {
        return (
            <motion.video
                data-testid={`hero-video-${kind}`}
                src={videoUrl}
                poster={image}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                initial={{ scale: 1.06, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                onError={() => setVideoUrl(null)}
                transition={{ duration: 2, ease: [0.16, 1, 0.3, 1] }}
                className={className}
            />
        );
    }
    return (
        <motion.img
            src={image}
            alt="Vantage Owners Club — Drop 001 campaign"
            initial={{ scale: 1.06 }}
            animate={{ scale: 1 }}
            transition={{ duration: 2, ease: [0.16, 1, 0.3, 1] }}
            className={className}
        />
    );
}

export default function Hero() {
    const ref = useRef(null);
    const overrides = useSiteImages();
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
    const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
    const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    return (
        <section ref={ref} data-testid="hero-section" className="relative h-[100svh] overflow-hidden">
            <motion.div style={{ y, scale }} className="absolute inset-3 overflow-hidden rounded-3xl sm:inset-0 sm:rounded-none">
                <HeroMedia kind="desktop" image={overrides.hero_desktop || MEDIA.desktop.image} className="hidden h-full w-full object-cover sm:block" />
                <HeroMedia kind="mobile" image={overrides.hero_mobile || MEDIA.mobile.image} className="block h-full w-full object-cover sm:hidden" />
                <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/10 to-black/70" />
            </motion.div>
            <motion.div style={{ opacity }} className="relative z-10 flex h-full flex-col items-center justify-end pb-24 text-center">
                <p className="mb-6 overflow-hidden text-[9px] uppercase tracking-[0.5em] text-[rgba(242,240,234,0.7)]">
                    <motion.span
                        className="block"
                        initial={{ y: "110%" }}
                        animate={{ y: "0%" }}
                        transition={{ delay: 1.6, duration: 1, ease: [0.16, 1, 0.3, 1] }}
                    >
                        Vantage Owners Club — Est. 2026
                    </motion.span>
                </p>
                <h1 className="font-display text-[11vw] font-black uppercase leading-[0.9] tracking-tighter text-[#F2F0EA] sm:text-[9vw]">
                    <Lines lines={["Own Your", "Perspective"]} delay={1.75} />
                </h1>
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 2.3, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                    className="mt-10"
                >
                    <Link
                        data-testid="hero-cta"
                        data-cursor="SHOP"
                        to="/shop?collection=Drop 001"
                        className="link-underline font-display text-[11px] font-bold uppercase tracking-[0.4em] text-[#F2F0EA]"
                    >
                        Explore Drop 001
                    </Link>
                </motion.div>
            </motion.div>
        </section>
    );
}
